import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const LINES = ["Achter ieder geluid,", "iedere song,", "is stilte."];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-40, 80]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0 -z-10" aria-hidden="true">
        <img
          src="https://images.pexels.com/photos/15039564/pexels-photo-15039564.jpeg"
          alt=""
          className="w-full h-[120%] object-cover contrast-125 saturate-50 opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-sand-50/60 via-transparent to-sand-50" />
      </motion.div>

      <div className="max-w-6xl mx-auto px-6 pt-24 w-full">
        <p className="overflow-hidden mb-6">
          <motion.span
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="block font-sans text-xs font-light tracking-[0.35em] uppercase text-terracotta-500"
          >
            Nederlandstalig · akoestisch · eigen werk
          </motion.span>
        </p>

        {LINES.map((line, i) => (
          <div key={line} className="overflow-hidden">
            <motion.h1
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1.1, delay: 0.3 + i * 0.16, ease: [0.16, 1, 0.3, 1] }}
              className="font-serif font-light tracking-tighter leading-[0.95] text-sand-900 text-6xl sm:text-7xl lg:text-8xl"
            >
              {line}
            </motion.h1>
          </div>
        ))}

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 1 }}
          className="mt-10 max-w-xl space-y-5 font-sans text-base font-light leading-relaxed text-sand-900/70"
        >
          <p>
            Een aphorisme uit de tijd dat ik leerling was in Guitar Craft:
            “Music is the Cup that holds the Wine of Silence.”
          </p>
          <p>
            Sinds die tijd ben ik meer en meer stilte gaan waarderen, en in mijn
            Zweedse jaren de luxe van de stilte nog meer gaan waarderen.
          </p>
          <p>
            Stil zijn in je zelf heeft hetzelfde effect als stilte om je heen:
            je gaat dingen in jou horen die anders overstemd worden door allerlei
            gedachten waar ik vaak, automatisch, onnodig aandacht aan geef.
          </p>
          <p>
            Als een muziekstuk ontstaat, merk ik dat stil zijn — waar nog geen
            invulling is gegeven — heel belangrijk is. De vraag: wat komt er
            hierna? Vaak werkt het… Het toelaten van, ruimte geven aan… Ruimte…
          </p>
        </motion.div>

        <motion.a
          href="#muziek"
          data-testid="hero-listen-cta"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="inline-flex items-center gap-3 mt-10 font-sans text-xs font-light tracking-[0.25em] uppercase text-sand-900/80 transition-colors hover:text-terracotta-500"
        >
          <span className="w-10 h-10 rounded-full border border-sand-900/30 flex items-center justify-center transition-colors duration-300 hover:border-terracotta-500">↓</span>
          Luister & lees
        </motion.a>
      </div>
    </section>
  );
};
